import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

/** Five throws, each beating two others — nothing is ever a safe pick. */
export const THROWS = ["Rock", "Paper", "Scissors", "Lizard", "Rocket"] as const;
export type Throw = (typeof THROWS)[number];

export const THROW_EMOJI: Record<Throw, string> = {
	Rock: "🪨",
	Paper: "📄",
	Scissors: "✂️",
	Lizard: "🦎",
	Rocket: "🚀",
};

/** What each throw beats, and the line the reveal screen reads out. */
export const BEATS: Record<Throw, { over: Throw; because: string }[]> = {
	Rock: [
		{ over: "Scissors", because: "Rock blunts Scissors" },
		{ over: "Lizard", because: "Rock squashes Lizard" },
	],
	Paper: [
		{ over: "Rock", because: "Paper wraps Rock" },
		{ over: "Rocket", because: "Paper plans the Rocket" },
	],
	Scissors: [
		{ over: "Paper", because: "Scissors cut Paper" },
		{ over: "Lizard", because: "Scissors snip Lizard" },
	],
	Lizard: [
		{ over: "Paper", because: "Lizard eats Paper" },
		{ over: "Rocket", because: "Lizard hides in the Rocket" },
	],
	Rocket: [
		{ over: "Rock", because: "Rocket blasts Rock" },
		{ over: "Scissors", because: "Rocket melts Scissors" },
	],
};

export const RPS_ROUNDS = 5;
export const RPS_PICK_MS = 8000;
export const RPS_REVEAL_MS = 3000;

export function beats(a: Throw, b: Throw): boolean {
	return BEATS[a].some((entry) => entry.over === b);
}

export interface RpsState extends BaseState {
	kind: "rps";
	round: number;
	phase: "pick" | "reveal";
	endsAt: number;
	picks: [number | null, number | null];
	/** Filled in during the reveal: who won and why. */
	lastRound: { winner: PlayerIndex | null; because: string } | null;
}

export interface RpsMove {
	pick: number;
}

function judge(state: RpsState, now: number): RpsState {
	const [first, second] = state.picks;
	const scores: [number, number] = [...state.scores];
	let lastRound: RpsState["lastRound"] = { winner: null, because: "Nobody threw anything!" };

	if (first !== null && second !== null) {
		const a = THROWS[first];
		const b = THROWS[second];
		if (a === b) {
			lastRound = { winner: null, because: `Both threw ${a} — a tie!` };
		} else if (beats(a, b)) {
			scores[0] += 1;
			lastRound = { winner: 0, because: BEATS[a].find((e) => e.over === b)?.because ?? "" };
		} else {
			scores[1] += 1;
			lastRound = { winner: 1, because: BEATS[b].find((e) => e.over === a)?.because ?? "" };
		}
	} else if (first !== null) {
		scores[0] += 1;
		lastRound = { winner: 0, because: "Too slow — no throw!" };
	} else if (second !== null) {
		scores[1] += 1;
		lastRound = { winner: 1, because: "Too slow — no throw!" };
	}

	return { ...state, scores, lastRound, phase: "reveal", endsAt: now + RPS_REVEAL_MS };
}

function nextRound(state: RpsState, now: number): RpsState {
	const round = state.round + 1;
	if (round >= RPS_ROUNDS) {
		return { ...state, result: resultFromScores(state.scores), endsAt: now };
	}
	return {
		...state,
		round,
		phase: "pick",
		endsAt: now + RPS_PICK_MS,
		picks: [null, null],
		lastRound: null,
	};
}

export const rockPaperScissors: GameDefinition<RpsState, RpsMove> = {
	id: "rps",
	category: "party",
	title: "Rock Paper Scissors Plus",
	emoji: "✊",
	blurb: "The classic, plus a lizard and a rocket. Best of five.",
	howTo: [
		"You both choose at the same time — nobody sees the other pick.",
		"Rock, Paper and Scissors work as always.",
		"Lizard beats Paper and Rocket. Rocket beats Rock and Scissors.",
		"Most wins after five rounds takes it.",
	],

	create(_rng, now) {
		return {
			kind: "rps",
			round: 0,
			phase: "pick",
			endsAt: now + RPS_PICK_MS,
			picks: [null, null],
			lastRound: null,
			// Both throw at once.
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const pick = (raw as { pick?: unknown }).pick;
		if (typeof pick !== "number" || !Number.isInteger(pick)) return null;
		if (pick < 0 || pick >= THROWS.length) return null;
		return { pick };
	},

	apply(state, player, move, now): MoveOutcome<RpsState> {
		if (state.result) return reject("This game is already finished.");
		if (state.phase !== "pick") return reject("Hold on — the throws are showing.");
		if (state.picks[player] !== null) return reject("You've already thrown.");

		const picks: [number | null, number | null] = [...state.picks];
		picks[player] = move.pick;
		const next = { ...state, picks };
		return { ok: true, state: picks[0] !== null && picks[1] !== null ? judge(next, now) : next };
	},

	legalMoves(state, player) {
		if (state.result || state.phase !== "pick" || state.picks[player] !== null) return [];
		return THROWS.map((_, pick) => ({ pick }));
	},

	tick(state, now) {
		if (state.result || now < state.endsAt) return null;
		return state.phase === "pick" ? judge(state, now) : nextRound(state, now);
	},

	nextTickAt(state) {
		return state.result ? null : state.endsAt;
	},

	redact(state, viewer) {
		if (state.phase === "reveal") return state;
		// Seeing the other throw before you commit would be the whole game.
		return {
			...state,
			picks: state.picks.map((pick, index) =>
				index === viewer ? pick : pick === null ? null : -1,
			) as [number | null, number | null],
		};
	},
};
